import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { selectAdminToken } from "../../admin/sessionSlice";
import { apiUrl } from "../../utils/apiUrl";
import pointsIcon from "../../img/points.png";
import timerIcon from "../../img/timer.png";
import {
  dedupeByUserIdBestScore,
  normalizeLeaderboardMatch,
} from "../leaderboard/LeaderboardLayout";

const MOBILE_MQ = "(max-width: 768px)";

function useIsMobileLayout() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(MOBILE_MQ).matches : false
  );

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const apply = () => setIsMobile(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  return isMobile;
}

function parseTimeToSeconds(t) {
  if (t == null) return 0;
  if (typeof t === "number" && Number.isFinite(t)) return Math.max(0, Math.floor(t));
  const s = String(t).trim();
  if (/^\d+$/.test(s)) return parseInt(s, 10) || 0;
  const parts = s.split(":").map((p) => parseInt(String(p).trim(), 10) || 0);
  if (parts.length >= 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length >= 2) return parts[0] * 60 + parts[1];
  return 0;
}

function formatDisplayTime(raw) {
  const toMmSs = (sec) => {
    const t = Math.max(0, Math.floor(sec));
    const m = Math.floor(t / 60);
    const ss = t % 60;
    return `${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  };
  if (raw == null || raw === "") return "00:00";
  if (typeof raw === "number" && Number.isFinite(raw)) return toMmSs(raw);
  const s = String(raw).trim();
  if (/^\d+$/.test(s)) return toMmSs(parseInt(s, 10) || 0);
  if (s.includes(":")) return s;
  return toMmSs(parseTimeToSeconds(s));
}

const PODIUM_THEMES = [
  { bg: "#fef9c3", border: "#eab308", badge: "#ca8a04" },
  { bg: "#dbeafe", border: "#3b82f6", badge: "#2563eb" },
  { bg: "#ffedd5", border: "#f97316", badge: "#ea580c" },
];

function PodiumCard({ player }) {
  const theme = PODIUM_THEMES[player.displayRank - 1] || PODIUM_THEMES[0];
  const isFirst = player.displayRank === 1;
  const rankClass =
    player.displayRank === 1
      ? " yg-lb-podium-card--first"
      : player.displayRank === 2
        ? " yg-lb-podium-card--rank-2"
        : player.displayRank === 3
          ? " yg-lb-podium-card--rank-3"
          : "";

  return (
    <div
      className={`yg-lb-podium-card${rankClass}`}
      style={{ background: theme.bg, borderColor: theme.border }}
    >
      {isFirst && (
        <span className="yg-lb-podium-crown" aria-hidden>
          👑
        </span>
      )}
      <div className="yg-lb-podium-badge" style={{ background: theme.badge }}>
        {player.displayRank}
      </div>
      <div className="yg-lb-podium-name">
        {player.name}
        {player.isSelf ? " (You)" : ""}
      </div>
      <div className="yg-lb-podium-stat">
        <img src={pointsIcon} alt="" className="yg-lb-stat-icon yg-lb-stat-icon--podium" />
        <span>{player.score ?? 0}</span>
      </div>
      <div className="yg-lb-podium-stat">
        <img src={timerIcon} alt="" className="yg-lb-stat-icon yg-lb-stat-icon--podium" />
        <span>{formatDisplayTime(player.time)}</span>
      </div>
    </div>
  );
}

function TableRow({ row, highlight, highlightStyle }) {
  return (
    <tr
      className={highlight ? "yg-lb-table-row--you" : undefined}
      style={highlight ? highlightStyle : undefined}
    >
      <td>{row.displayRank}</td>
      <td>{highlight ? "You" : row.name}</td>
      <td>
        <span className="yg-lb-table-stat">
          <img src={pointsIcon} alt="" className="yg-lb-stat-icon" />
          <span>{row.score ?? 0}</span>
        </span>
      </td>
      <td>
        <span className="yg-lb-table-stat">
          <img src={timerIcon} alt="" className="yg-lb-stat-icon" />
          <span>{formatDisplayTime(row.time)}</span>
        </span>
      </td>
    </tr>
  );
}

export default function TalentLeaderboardModal({
  open,
  onClose,
  surfaceStyle,
  titleColor,
  highlightStyle,
}) {
  const user = useSelector((s) => s.auth.user);
  const adminToken = useSelector(selectAdminToken);
  const isMobile = useIsMobileLayout();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");

  useEffect(() => {
    if (!open || !user || !adminToken) return undefined;

    let cancelled = false;
    (async () => {
      setLoading(true);
      setFetchError("");
      try {
        const uid = user.userId || user.id;
        const resp = await fetch(apiUrl("/gameMatchUsers"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId: user.sessionId,
            organizationId: user.organizationId,
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (cancelled) return;
        if (!resp.ok || !data.success) {
          setMatches([]);
          setFetchError(data.error || data.message || "Could not load leaderboard");
          return;
        }
        const raw = Array.isArray(data.matches) ? data.matches : [];
        setMatches(dedupeByUserIdBestScore(raw.map(normalizeLeaderboardMatch)));
      } catch (e) {
        if (!cancelled) {
          setMatches([]);
          setFetchError(e.message || "Network error");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, user, adminToken]);

  const sortedRows = useMemo(() => {
    const copy = [...matches];
    copy.sort((a, b) => {
      const sb = b.score ?? 0;
      const sa = a.score ?? 0;
      if (sb !== sa) return sb - sa;
      return parseTimeToSeconds(a.time) - parseTimeToSeconds(b.time);
    });
    return copy.map((m, i) => ({ ...m, displayRank: i + 1 }));
  }, [matches]);

  const myId = String(user?.userId || user?.id || "");
  const me = useMemo(
    () =>
      sortedRows.find((r) => String(r.userId || "") === myId) ||
      sortedRows.find((r) => r.isSelf) ||
      null,
    [sortedRows, myId]
  );

  const topThree = sortedRows.slice(0, 3);
  const podiumOrder = topThree.length === 3 ? [1, 0, 2] : topThree.length === 2 ? [1, 0] : [0];
  const podiumSlots = podiumOrder.map((i) => topThree[i]).filter(Boolean);

  const tableRows = useMemo(() => {
    const rows = sortedRows.slice(3, 5);
    const meInTable = rows.some((r) => String(r.userId || "") === myId);
    if (me && me.displayRank > 5 && !meInTable) {
      return [...rows, { ...me, name: "You", isSelf: true }];
    }
    return rows;
  }, [sortedRows, me, myId]);

  const tableHeadStyle = useMemo(() => {
    if (!highlightStyle?.backgroundColor && !highlightStyle?.color) return undefined;
    return {
      ...(highlightStyle.backgroundColor && { "--yg-lb-th-bg": highlightStyle.backgroundColor }),
      ...(highlightStyle.color && { "--yg-lb-th-color": highlightStyle.color }),
    };
  }, [highlightStyle]);

  if (!open) return null;

  return (
    <div className="yg-lb-backdrop" onClick={onClose} role="presentation">
      <div
        className={`yg-lb-modal${isMobile ? " yg-lb-modal--mobile" : ""}${
          surfaceStyle ? " yg-surface--themed" : ""
        }`}
        role="dialog"
        aria-labelledby="yg-lb-title"
        style={surfaceStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="yg-lb-modal-header">
          <h2 id="yg-lb-title" className="yg-lb-modal-title" style={{ color: titleColor }}>
            Leaderboard
          </h2>
          <button type="button" className="yg-lb-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        {loading ? (
          <div className="quiz-loader-container yg-lb-loader">
            <div className="quiz-loader" />
          </div>
        ) : fetchError ? (
          <p className="yg-lb-error">{fetchError}</p>
        ) : sortedRows.length === 0 ? (
          <p className="yg-lb-empty">No scores yet. Complete the yoga poses game first!</p>
        ) : (
          <>
            {podiumSlots.length > 0 && (
              <div className="yg-lb-podium" aria-label="Top three players">
                {podiumSlots.map((p) => (
                  <PodiumCard key={p.userId || `${p.name}-${p.displayRank}`} player={p} />
                ))}
              </div>
            )}

            {tableRows.length > 0 && (
              <div className="yg-lb-table-wrap">
                <table
                  className={`yg-lb-table${tableHeadStyle ? " yg-lb-table--themed-head" : ""}`}
                  style={tableHeadStyle}
                >
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Player Name</th>
                      <th>Score</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map((row) => (
                      <TableRow
                        key={row.userId || `rank-${row.displayRank}`}
                        row={row}
                        highlight={Boolean(row.isSelf)}
                        highlightStyle={highlightStyle}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
